/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/

import "./App.scss";

import { BrowserAuthorizationClient } from "@itwin/browser-authorization";
import { EmphasizeElements, IModelConnection, PerModelCategoryVisibility, ScreenViewport, Viewport, ViewState3d } from "@itwin/core-frontend";
import { FitViewTool, IModelApp, StandardViewId } from "@itwin/core-frontend";
import { FillCentered } from "@itwin/core-react";
import { ProgressLinear } from "@itwin/itwinui-react";
import {
  MeasureTools,
  MeasureToolsUiItemsProvider,
} from "@itwin/measure-tools-react";
import {
  PropertyGridManager,
  PropertyGridUiItemsProvider,
} from "@itwin/property-grid-react";
import {
  useAccessToken,
  Viewer,
  ViewerContentToolsProvider,
  ViewerNavigationToolsProvider,
  ViewerPerformance,
  ViewerStatusbarItemsProvider,
  ViewerViewportControlOptions,
} from "@itwin/web-viewer-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { history } from "./history";
import { PipeData } from "./PipeRunList/PipeData";
import { PipeListUiProvider } from "./PipeRunList/PipeListUiProvider";
import { ScannerUiProvider } from "./QrScanner/ScannerUiProvider";
import { ViewSetup } from "./SavedView/ViewSetup";
import { NorthArrowDecorator, createNorthArrowDecorator } from "./NorthArrowDecorator";
import { SpoolBoxDecorator } from "./Utils/SpoolBoxDecorator";
import { SpoolData } from "./SpoolBox/SpoolData";
import { PipeSpoolsDecorator } from "./PipeSpoolsDecorator";
import { ControlValveDecorator } from "./Utils/ControlValveDecorator";
import { InstrumentDecorator } from "./Utils/InstrumentDecorator";
import { ToggleWidgetProvider } from "./Toggle/ToggleWidget";
import { SmartEquipmentData } from "./SmartEquipment/SmartEquipmentData";
import { EquipmentDecorator } from "./Utils/EquipmentDecorator";


const App: React.FC = () => {
  const [iModelId, setIModelId] = useState(process.env.IMJS_IMODEL_ID ?? "");
  const [iTwinId, setITwinId] = useState(process.env.IMJS_ITWIN_ID ?? "");

  const accessToken = useAccessToken();


  /** Client that implements the ViewerAuthorizationClient interface */
  const authClient = useMemo(
    () =>
      new BrowserAuthorizationClient({
        scope: process.env.IMJS_AUTH_CLIENT_SCOPES ?? "",
        clientId: process.env.IMJS_AUTH_CLIENT_CLIENT_ID ?? "",
        redirectUri: process.env.IMJS_AUTH_CLIENT_REDIRECT_URI ?? "",
        postSignoutRedirectUri: process.env.IMJS_AUTH_CLIENT_LOGOUT_URI,
        responseType: "code",
        authority: process.env.IMJS_AUTH_AUTHORITY,
      }),
    []
  );

  const login = useCallback(async () => {
    try {
      await authClient.signInSilent();
    } catch {
      await authClient.signIn();
    }
  }, [authClient]);

  useEffect(() => {
    void login();
  }, [login]);

  useEffect(() => {
    if (accessToken) {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has("iTwinId")) {
        setITwinId(urlParams.get("iTwinId") as string);
      } else {
        if (!process.env.IMJS_ITWIN_ID) {
          throw new Error(
            "Please add a valid iTwin ID in the .env file and restart the application or add it to the iTwinId query parameter in the url and refresh the page. See the README for more information."
          );
        }
      }

      if (urlParams.has("iModelId")) {
        setIModelId(urlParams.get("iModelId") as string);
      }
    }
  }, [accessToken]);

  useEffect(() => {
    if (accessToken && iTwinId) {
      let queryString = `?iTwinId=${iTwinId}`;
      if (iModelId) {
        queryString += `&iModelId=${iModelId}`;
      }

      history.push(queryString);
    }
  }, [accessToken, iTwinId, iModelId]);

  /** optional function to configure the viewport on load */
  const viewConfiguration = useCallback((viewPort: ScreenViewport) => {
    // default execute the fitview tool and use the iso standard view after tile trees are loaded
    const tileTreesLoaded = () => {
      return new Promise((resolve, reject) => {
        const start = new Date();
        const intvl = setInterval(() => {
          if (viewPort.areAllTileTreesLoaded) {
            ViewerPerformance.addMark("TilesLoaded");
            ViewerPerformance.addMeasure(
              "TileTreesLoaded",
              "ViewerStarting",
              "TilesLoaded"
            );
            clearInterval(intvl);
            resolve(true);
          }
          const now = new Date();
          // after 20 seconds, stop waiting and fit the view
          if (now.getTime() - start.getTime() > 20000) {
            reject();
          }
        }, 100);
      });
    };

    tileTreesLoaded().finally(() => {
      void IModelApp.tools.run(FitViewTool.toolId, viewPort, true, false);
      viewPort.view.setStandardRotation(StandardViewId.Iso);
    });
  }, []);


  /** Options for creating the default viewState */
  const viewCreatorOptions = useMemo(
    () => ({ viewportConfigurer: viewConfiguration }),
    [viewConfiguration]
  );


  /** Setup the overrides of the viewport based on the saved view */
  const applyOverrides = async (view: any, vp: Viewport) => {
    // Clear the current if there's any
    if (EmphasizeElements.get(vp)) {
      EmphasizeElements.clear(vp);
      vp.isFadeOutActive = false;
    }
    // Setup from props
    const props = view.emphasizeElementsProps;
    if (props) {
      EmphasizeElements.getOrCreate(vp).fromJSON(props, vp);
    }

    if ((view as any).perModelCategoryVisibility !== undefined) {
      // Setup per model category visibility
      const view3d = view;
      vp.perModelCategoryVisibility.clearOverrides();
      if (view3d.perModelCategoryVisibility) {
        for (const override of view3d.perModelCategoryVisibility) {
          vp.perModelCategoryVisibility.setOverride(
            override.modelId,
            override.categoryId,
            override.visible
              ? PerModelCategoryVisibility.Override.Show
              : PerModelCategoryVisibility.Override.Hide
          );
        }
      }
    }
  }

  /** Makes ascetic changes to the default view */
  const applyView = async () => {
    try {
      const vp = IModelApp.viewManager.selectedView;

      // Getting the savedView and viewState from ViewSetup 
      const savedView = ViewSetup.savedViewObject;
      const viewState = ViewSetup.viewStateObject;

      if (vp) {
  
        if (viewState) {
          if (vp.view.is3d()) {
            const backgroundMapProps =
              viewState.displayStyle.backgroundMapSettings.toJSON();
            backgroundMapProps.groundBias =
              vp.view.displayStyle.backgroundMapSettings.groundBias;
            backgroundMapProps.planarClipMask =
              vp.view.displayStyle.backgroundMapSettings.planarClipMask?.toJSON();
            viewState.displayStyle.changeBackgroundMapProps(backgroundMapProps);
          }
          if (vp.view instanceof ViewState3d && vp.view.displayStyle.scheduleScript) {
            await (viewState as ViewState3d)
              .getDisplayStyle3d()
              .changeRenderTimeline(vp.view.displayStyle.settings.renderTimeline);
          }
          
          vp.changeView(viewState);          
          await applyOverrides(savedView, vp);

        }
      }
    } catch (error) {
      console.log(`*** error ***, ${error}`);
    }
  }  

 /** Callback function that executes after IModelApp.startup completes */ 
 const onIModelAppInit = useCallback(async () => {
    await PropertyGridManager.initialize();
    await MeasureTools.startup();
    IModelApp.viewManager.onViewOpen.addOnce(PipeData.initializeLink);
    IModelApp.viewManager.onViewOpen.addListener(SpoolData.initializeLink);
    IModelApp.viewManager.onViewOpen.addOnce(SmartEquipmentData.initializeLink);
  },[]);

  /** Callback function that executes after the iModel connection is successful and contains the iModel connection as a parameter */
  const onIModelConnected = async (iModel: IModelConnection) => {
    localStorage.removeItem('Spools');
    localStorage.removeItem('Instrument');
    localStorage.removeItem('Equipment');
    localStorage.removeItem('ControlValve');
    IModelApp.viewManager.onViewOpen.addOnce(applyView);
    IModelApp.viewManager.onViewOpen.addOnce(addDecorator);
    // start creating the decorator and loading the texture
    createNorthArrowDecorator(NorthArrowDecorator, iModel).then((decorator) => {
      // test that decorator was created (dependant on texture creation)
      if (decorator === undefined)
        return;

      // add decorator to view manager for rendering
      const dropDecorator = IModelApp.viewManager.addDecorator(decorator);

      // Clean resources (texture) on close
      iModel.onClose.addOnce(() => {
        dropDecorator();
        decorator.dispose();
      })
    });
  }

  /** Additional options for the default frontstage's IModelViewportControl */
  const viewportOptionsConfiguration = ViewSetup.getDefaultView;

   const viewportOptions : ViewerViewportControlOptions = useMemo(
    () => ({ viewState: viewportOptionsConfiguration }),
    [viewportOptionsConfiguration]
  );

  const addDecorator = (_vp: ScreenViewport) => {
    //const decorator = new NorthArrowDecorator();
    
    const spooldeocorator = new SpoolBoxDecorator(_vp);
    const pipeSpoolDecorator = new PipeSpoolsDecorator(_vp);
    const contorlvalveDecorator = new ControlValveDecorator(_vp);
    const insturmentvalvedecortor = new InstrumentDecorator(_vp);
    const equipmentDecorator = new EquipmentDecorator(_vp);
    //IModelApp.viewManager.addDecorator(decorator);
    IModelApp.viewManager.addDecorator(spooldeocorator);
    //IModelApp.viewManager.dropDecorator(pipeSpoolDecorator);
    IModelApp.viewManager.addDecorator(pipeSpoolDecorator);
    IModelApp.viewManager.addDecorator(contorlvalveDecorator);
    IModelApp.viewManager.addDecorator(insturmentvalvedecortor);
    IModelApp.viewManager.addDecorator(equipmentDecorator);
  }
  return (
    <div className="viewer-container">
      {!accessToken && (
        <FillCentered>
          <div className="signin-content">
            <ProgressLinear indeterminate={true} labels={["Signing in..."]} />
          </div>
        </FillCentered>
      )}
      <Viewer
        iTwinId={iTwinId ?? ""}
        iModelId={iModelId ?? ""}
        authClient={authClient}
        viewCreatorOptions={viewCreatorOptions}
        viewportOptions={viewportOptions}
        enablePerformanceMonitors={false} // see description in the README (https://www.npmjs.com/package/@itwin/web-viewer-react)
        onIModelAppInit={onIModelAppInit}
        onIModelConnected={onIModelConnected}
        // defaultUiConfig={
        //     {
        //       hideNavigationAid: true,
        //       hideStatusBar: true,
        //       hideToolSettings: true,
        //     }
        // }
        uiProviders={[
          new ViewerNavigationToolsProvider(),
          new ToggleWidgetProvider(),
          new ViewerContentToolsProvider({
            vertical: {
              measureGroup: false,
            },
            horizontal: {
              clearSelection: true,
              clearDisplayOverrides: true
            }
          }),
          new ViewerStatusbarItemsProvider(),
          new PropertyGridUiItemsProvider({
            enableCopyingPropertyText: true,
          }),
          new MeasureToolsUiItemsProvider(),
          new PipeListUiProvider(),
          new ScannerUiProvider(),
          
        ]
      }
      />
    </div>
  );
};

export default App;
